import React, { useEffect, useCallback, useMemo } from "react";
import { useBoolean, useMount, useUpdateEffect } from "ahooks";
import cz from "classnames";
import { Badge } from "antd-mobile";
import { useHistory, useLocation } from "react-router-dom";
import TrackContainer from "@/components/TrackContainer";
import ErrorBoundary from "@/components/ErrorBoundary";
import {
  dispatchSDK,
  getQueryObject,
  removeUrlParameter,
} from "@/utils/extend";
import useTabMenus from "@/hooks/useTabMenus";
import TabbarContext from "./ctx";
import IconFont from "../IconFont";
import "./style.scss";
import { getUserBasicInfo } from "@/service/account";
import { reportCommunityEvent } from "@/service/community";
import useAppStore from "@/store/useAppStore";

export type IPosition = "top" | "left" | "bottom";

interface TabBarProps {
  position?: IPosition;
  children: React.ReactNode;
}

const TabBar: React.FC<TabBarProps> = (props) => {
  const { children, position = "bottom" } = props;
  const { source } = getQueryObject(window.location.href);
  const unreadFeedbackCount = useAppStore(
    (state) => state.unreadMessages.feedback
  );
  const getUnreadMessages = useAppStore((state) => state.getUnreadMessages);
  const { menus, hasMenuBar } = useTabMenus({
    source,
    onSuccess: () => {
      reportPageEvent();
      reaportCommunityExposure();
    },
  });

  const menusWithUnread = useMemo(() => {
    return menus?.map((k) => ({
      ...k,
      count: k.key === "my" ? unreadFeedbackCount : 0,
    }));
  }, [menus, unreadFeedbackCount]);

  const history = useHistory();
  const location = useLocation();

  const [keepMargin, { toggle: setKeepMargin }] = useBoolean(true);

  const generateBasicReportParams = async () => {
    const userInfo = await getUserBasicInfo();
    const { account, game_id } = userInfo?.data ?? {};
    return {
      account,
      gameid: Number(game_id),
      posttime: Math.floor(new Date().getTime() / 1000),
    };
  };

  const go = (url: string) => {
    const hasVistedPageDetail = localStorage.getItem("hasVistedPageDetail");
    if (hasVistedPageDetail === "1" && url !== "community") {
      const nextUrl = removeUrlParameter(window.location.href, "postsid");
      window.history.replaceState({}, document.title, nextUrl);
      history.push({
        pathname: `/${url}`,
        search: nextUrl.split("?")[1],
      });
      localStorage.setItem("hasVistedPageDetail", "0");
    } else {
      history.push({
        pathname: `/${url}`,
        search: location.search,
      });
    }
  };

  // 社区入口曝光 上报
  const reaportCommunityExposure = async () => {
    try {
      if (!menus || menus.length === 0) return;
      const hasCommunity =
        menus?.find((k) => k.key === "community") !== undefined;
      if (!hasCommunity) return;
      const basic = await generateBasicReportParams();
      const data = {
        ...basic,
        eventname: "community_entrance_view",
        properties: JSON.stringify({ pv_id: "sdk入口", sdk_paltform: "sdk" }),
      };
      console.log("pageEvent: 社区入口曝光上报", data);
      reportCommunityEvent(data);
    } catch (err) {
      console.log(err);
    }
  };

  // 社区入口点击 上报
  const reportCommunityClick = async () => {
    const url = history.location.pathname.toLowerCase().slice(1);
    if (url.includes("community")) {
      const basic = await generateBasicReportParams();
      const data = {
        ...basic,
        eventname: "community_entrance_click",
        properties: JSON.stringify({
          view_id: "sdk入口",
          click_type: "入口icon",
          click_result: "成功",
          sdk_paltform: "sdk",
        }),
      };
      console.log("pageEvent: 社区入口点击上报", data);
      reportCommunityEvent(data);
    }
  };

  const reportPageEvent = useCallback(() => {
    try {
      if (!menus || menus.length === 0) return;
      const url = history.location.pathname.toLowerCase().slice(1);
      const serverUrl = menus?.find(
        (i: any) => i.url.split("/").slice(-1)[0].toLowerCase() === url
      )?.key;
      const event = `h5_nav_${serverUrl}`;
      const timestamp = Math.round(new Date().getTime() / 1000);
      const data = JSON.stringify({
        timestamp,
        event,
        properties: { source: "load" },
      });
      console.log("pageEvent:", data);
      dispatchSDK("reportEvent", data);
      dispatchSDK("callBackFloatUrl", window.location.href);
    } catch (err) {
      console.log(err);
    }
  }, [history.location.pathname, menus]);

  useUpdateEffect(() => {
    reportPageEvent();
    reportCommunityClick();
  }, [history.location.pathname]);

  useEffect(() => {
    document.body.classList.add(position);
  }, [position]);

  useMount(() => {
    // 不可移除：部分页面如隐私协议没有token
    if (hasMenuBar) {
      getUnreadMessages();
    }
  });

  return (
    <TabbarContext.Provider
      value={{
        keepMargin,
        setKeepMargin,
      }}
    >
      <div className="tabbar">
        <div className={`tabbar-tabs tabbar-${position}`}>
          {hasMenuBar && (
            <div className="tabbar-tabs__bar">
              {menusWithUnread?.map((i: any) => (
                <TrackContainer
                  key={i.key}
                  trackId={`h5_nav_${i.key}`}
                  properties={{ source: "click" }}
                >
                  <div
                    key={i.key}
                    className={cz("tabbar-tabs__item", {
                      active:
                        i.url.toLowerCase() ===
                        window.location.pathname.toLowerCase(),
                    })}
                    onClick={() => {
                      go(i.url.split("/").slice(-1)[0].toLowerCase());
                    }}
                  >
                    <div className="tabbar-tabs__itemInner">
                      <Badge
                        className="tabbar-tabs__badge"
                        text={i.count}
                        overflowCount={99}
                        dot={!!i.count}
                      >
                        <IconFont name={`nav-${i.key}`} size={18} />
                        <div className="tabbar-tabs__name">{i.name}</div>
                      </Badge>
                    </div>
                  </div>
                </TrackContainer>
              ))}
            </div>
          )}

          <div
            className="tabbar-tabs__content"
            style={{
              marginBottom: hasMenuBar && keepMargin ? 9 : 0,
            }}
          >
            <ErrorBoundary>{children}</ErrorBoundary>
          </div>
        </div>
      </div>
    </TabbarContext.Provider>
  );
};

export default TabBar;
